<template>
    <div v-if="createdBot" class="step-complete">
        <div class="success-icon">
            <i class="el-icon-success"></i>
        </div>
        
        <h4 class="step-title">{{ activated ? 'IVA Created & Activated!' : 'IVA Draft Created!' }}</h4>
        
        <p class="step-description">
            {{ activated 
                ? 'Your IVA has been successfully created and is now handling calls on your line.' 
                : 'Your IVA has been created as a draft. Follow the steps below to activate it.' 
            }}
        </p>

        <!-- Activated Flow -->
        <template v-if="activated">
            <div class="info-card mb-3">
                <h5>
                    <i class="el-icon-link"></i>
                    Created Agent
                </h5>
                <div class="agent-link-card">
                    <div class="agent-info">
                        <strong>{{ createdBot.name }}</strong>
                        <div class="text-muted small">
                            Voice Agent • {{ getLanguageName(createdBot.language) }}
                        </div>
                    </div>
                    <router-link 
                        :to="`/aloai/${createdBot.id}`"
                        class="btn btn-sm btn-primary">
                        <i class="fa fa-edit"></i>
                        Open Agent
                    </router-link>
                </div>
            </div>

            <div class="info-card mb-3">
                <h5>
                    <i class="el-icon-check"></i>
                    What Changed
                </h5>
                <div class="changes-list">
                    <div class="change-item">
                        <i class="el-icon-phone"></i>
                        <div>
                            <strong>Line Configuration Updated</strong>
                            <div class="text-muted small">
                                Your line "{{ lineName }}" is now using the IVA agent instead of the legacy IVR.
                            </div>
                        </div>
                    </div>
                    <div class="change-item">
                        <i class="el-icon-circle-check"></i>
                        <div>
                            <strong>IVA Activated</strong>
                            <div class="text-muted small">
                                The agent is enabled and ready to handle incoming calls.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Draft Flow -->
        <template v-else>
            <div class="info-card mb-3">
                <h5>
                    <i class="el-icon-link"></i>
                    Draft Agent
                </h5>
                <div class="agent-link-card">
                    <div class="agent-info">
                        <strong>{{ createdBot.name }}</strong>
                        <div class="text-muted small">
                            Voice Agent • {{ getLanguageName(createdBot.language) }}
                        </div>
                    </div>
                    <router-link 
                        :to="`/aloai/${createdBot.id}`"
                        class="btn btn-sm btn-primary">
                        <i class="fa fa-edit"></i>
                        Open Agent
                    </router-link>
                </div>
            </div>

            <div class="info-card mb-3">
                <h5>
                    <i class="el-icon-info"></i>
                    Steps to Activate
                </h5>
                <div class="steps-list">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <strong>Review Your Agent</strong>
                            <div class="text-muted small">
                                Click "Open Agent" above to review and customize the IVA's instructions, voice, and settings
                            </div>
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <strong>Go to Line Settings</strong>
                            <div class="text-muted small">
                                Navigate to <strong>Lines → {{ lineName }} → Routing & IVR</strong>
                            </div>
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <strong>Attach the Agent</strong>
                            <div class="text-muted small">
                                In the "Who should answer incoming calls?" section, select <strong>An AloAi Agent</strong> and choose <strong>{{ createdBot.name }}</strong>
                            </div>
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">4</div>
                        <div class="step-content">
                            <strong>Save Changes</strong>
                            <div class="text-muted small">
                                Click "Save" to activate your IVA on the line
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

    </div>
</template>

<script>
export default {
    name: 'Step5Complete',

    props: {
        createdBot: {
            type: Object,
            default: null
        },
        activated: {
            type: Boolean,
            default: false
        },
        lineName: {
            type: String,
            default: ''
        }
    },

    methods: {
        getLanguageName(code) {
            if (!code) return 'English'
            
            const languages = {
                'en': 'English',
                'en-US': 'English (US)',
                'en-GB': 'English (UK)',
                'es': 'Spanish',
                'es-ES': 'Spanish (Spain)',
                'es-MX': 'Spanish (Mexico)',
                'fr': 'French',
                'fr-FR': 'French (France)',
                'fr-CA': 'French (Canadian)',
                'de': 'German',
                'de-DE': 'German',
                'it': 'Italian',
                'it-IT': 'Italian',
                'pt': 'Portuguese',
                'pt-BR': 'Portuguese (Brazilian)',
                'pt-PT': 'Portuguese (European)',
                'zh': 'Chinese',
                'zh-CN': 'Chinese (Simplified)',
                'zh-TW': 'Chinese (Traditional)',
                'ja': 'Japanese',
                'ja-JP': 'Japanese',
                'ko': 'Korean',
                'ko-KR': 'Korean',
            }
            
            if (languages[code]) {
                return languages[code]
            }
            
            if (languages[code.toLowerCase()]) {
                return languages[code.toLowerCase()]
            }
            
            if (code.includes('-')) {
                const baseLanguage = code.split('-')[0].toLowerCase()
                if (languages[baseLanguage]) {
                    return languages[baseLanguage]
                }
            }
            
            return 'English'
        },

        handleDone() {
            this.$emit('done')
        }
    }
}
</script>

<style scoped>
.step-complete {
    padding: 10px 20px;
    padding-bottom: 0px;
    text-align: center;
}

.success-icon {
    font-size: 80px;
    color: #67c23a;
    margin-bottom: 10px;
}

.step-title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #303133;
}

.step-description {
    color: #666;
    margin-bottom: 30px;
    font-size: 15px;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.info-card {
    background: #f9f9f9;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 20px;
    text-align: left;
}

.info-card h5 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 8px;
}

.agent-link-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
}

.agent-info strong {
    display: block;
    font-size: 15px;
    color: #303133;
    margin-bottom: 4px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
}

.changes-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.change-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
}

.change-item > i {
    font-size: 20px;
    color: #67c23a;
    margin-top: 2px;
}

.change-item strong {
    display: block;
    font-size: 14px;
    color: #303133;
    margin-bottom: 4px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
}

.change-item .text-muted {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.steps-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.step-item {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    padding: 15px;
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
}

.step-number {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background: #409eff;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 16px;
}

.step-content {
    flex: 1;
}

.step-content strong {
    display: block;
    font-size: 14px;
    color: #303133;
    margin-bottom: 4px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
}

.step-content .text-muted {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.text-muted {
    color: #909399;
}

.small {
    font-size: 13px;
}
</style>

